import React, { useMemo, useState, createContext } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { initializeApp } from 'firebase/app';
import { config } from './config/config';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { getTheme } from './theme';
import '@fontsource/nunito/300.css';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/500.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import './index.css';
import AppBackground from './components/AppBackground';

initializeApp(config.firebaseConfig);

export const ColorModeContext = createContext({ toggleColorMode: () => {} });

type ColorMode = 'light' | 'dark';
const COLOR_MODE_KEY = 'colorMode';

// A saved choice wins; otherwise follow the device setting.
const initialColorMode = (): ColorMode => {
  try {
    const saved = localStorage.getItem(COLOR_MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Fall through to the system preference
  }
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
};

const AppWrapper = () => {
  const [mode, setMode] = useState<ColorMode>(initialColorMode);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prevMode) => {
          const next = prevMode === 'light' ? 'dark' : 'light';
          try {
            localStorage.setItem(COLOR_MODE_KEY, next);
          } catch {
            // Storage can be blocked (private browsing); the toggle still works for this visit
          }
          return next;
        }),
    }),
    []
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppBackground />
        <App />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);

reportWebVitals();